import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo2,
  Redo2,
} from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const PARAGRAPH_STYLE_OPTIONS = [
  { value: "paragraph", level: 0 },
  { value: "h1", level: 1 },
  { value: "h2", level: 2 },
  { value: "h3", level: 3 },
];

/**
 * Standard paragraph/text formatting editor (bold, italic, underline,
 * headings, alignment, lists) — deliberately no font-family/size picker.
 * Built on Tiptap 3, same document/paragraph/text/history base as
 * BoldOnlyEditor but with StarterKit's structural extensions left enabled
 * (heading capped to h1-h3, strike/code/codeBlock/blockquote/hr/link
 * disabled — none of those are offered in this toolbar).
 *
 * `availableFields`: [{key, label}] — populates the "Insert field" dropdown,
 * which inserts a `{{field_key}}` placeholder token at the cursor.
 */
export function HtmlBlockEditor({ value, onChange, availableFields = [], disabled = false, testId = "html-block-editor" }) {
  const { t } = useI18n();
  const hasMounted = useRef(false);
  const [insertKey, setInsertKey] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: false,
      }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    editable: !disabled,
    editorProps: {
      attributes: {
        // Tailwind's preflight resets `ul`/`ol` to list-style:none and
        // headings to inherit font-size/weight — restore both so the
        // WYSIWYG area actually looks like the bullets/headings it represents
        // (the rendered preview, a bare srcDoc iframe with no Tailwind, is
        // unaffected and never needed this).
        class:
          "min-h-[280px] px-3 py-2 text-sm leading-relaxed outline-none " +
          "[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:my-0.5 " +
          "[&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold",
        "data-testid": `${testId}-area`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      if (!hasMounted.current) return;
      const html = ed.getHTML();
      onChange(html.replace(/\s+/g, "") === "<p></p>" ? "" : html);
    },
    onCreate: () => {
      hasMounted.current = true;
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    const currentEmpty = current.replace(/\s+/g, "") === "<p></p>";
    const incomingEmpty = incoming.replace(/\s+/g, "") === "" || incoming.replace(/\s+/g, "") === "<p></p>";
    if ((currentEmpty && incomingEmpty) || current === incoming) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const state = useEditorState({
    editor,
    selector: (ctx) => {
      if (!ctx.editor) {
        return { bold: false, italic: false, underline: false, bulletList: false, orderedList: false, paragraphStyle: "paragraph", align: "left" };
      }
      const e = ctx.editor;
      const paragraphStyle = PARAGRAPH_STYLE_OPTIONS.find(
        (o) => o.level > 0 && e.isActive("heading", { level: o.level })
      )?.value || "paragraph";
      const align = ["left", "center", "right"].find((a) => e.isActive({ textAlign: a })) || "left";
      return {
        bold: e.isActive("bold"),
        italic: e.isActive("italic"),
        underline: e.isActive("underline"),
        bulletList: e.isActive("bulletList"),
        orderedList: e.isActive("orderedList"),
        paragraphStyle,
        align,
      };
    },
  });

  if (!editor) return null;

  const run = (fn) => () => {
    if (disabled) return;
    fn();
  };

  const setParagraphStyle = (v) => {
    const opt = PARAGRAPH_STYLE_OPTIONS.find((o) => o.value === v);
    if (!opt || opt.level === 0) editor.chain().focus().setParagraph().run();
    else editor.chain().focus().toggleHeading({ level: opt.level }).run();
  };

  const insertField = (fieldKey) => {
    editor.chain().focus().insertContent(`{{${fieldKey}}}`).run();
    setInsertKey((k) => k + 1); // remounts the Select so it shows the placeholder again
  };

  return (
    <div className="rounded-md border border-input bg-background" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b border-border px-2 py-1.5">
        <Select value={state.paragraphStyle} onValueChange={setParagraphStyle} disabled={disabled}>
          <SelectTrigger className="h-7 w-28 text-xs" data-testid={`${testId}-paragraph-style`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paragraph">{t("htmlEditor.normal")}</SelectItem>
            <SelectItem value="h1">{t("htmlEditor.heading1")}</SelectItem>
            <SelectItem value="h2">{t("htmlEditor.heading2")}</SelectItem>
            <SelectItem value="h3">{t("htmlEditor.heading3")}</SelectItem>
          </SelectContent>
        </Select>

        <div className="mx-1 h-5 w-px bg-border" />

        <Button type="button" variant={state.bold ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().toggleBold().run())} disabled={disabled}
                data-testid={`${testId}-bold-btn`} aria-pressed={state.bold} aria-label={t("boldEditor.bold")}>
          <BoldIcon className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant={state.italic ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().toggleItalic().run())} disabled={disabled}
                data-testid={`${testId}-italic-btn`} aria-pressed={state.italic} aria-label={t("htmlEditor.italic")}>
          <ItalicIcon className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant={state.underline ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().toggleUnderline().run())} disabled={disabled}
                data-testid={`${testId}-underline-btn`} aria-pressed={state.underline} aria-label={t("htmlEditor.underline")}>
          <UnderlineIcon className="h-3.5 w-3.5" />
        </Button>

        <div className="mx-1 h-5 w-px bg-border" />

        <Button type="button" variant={state.bulletList ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().toggleBulletList().run())} disabled={disabled}
                data-testid={`${testId}-bullet-list-btn`} aria-pressed={state.bulletList} aria-label={t("htmlEditor.bulletList")}>
          <ListIcon className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant={state.orderedList ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().toggleOrderedList().run())} disabled={disabled}
                data-testid={`${testId}-ordered-list-btn`} aria-pressed={state.orderedList} aria-label={t("htmlEditor.orderedList")}>
          <ListOrderedIcon className="h-3.5 w-3.5" />
        </Button>

        <div className="mx-1 h-5 w-px bg-border" />

        <Button type="button" variant={state.align === "left" ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().setTextAlign("left").run())} disabled={disabled}
                data-testid={`${testId}-align-left-btn`} aria-pressed={state.align === "left"} aria-label={t("htmlEditor.alignLeft")}>
          <AlignLeft className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant={state.align === "center" ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().setTextAlign("center").run())} disabled={disabled}
                data-testid={`${testId}-align-center-btn`} aria-pressed={state.align === "center"} aria-label={t("htmlEditor.alignCenter")}>
          <AlignCenter className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant={state.align === "right" ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().setTextAlign("right").run())} disabled={disabled}
                data-testid={`${testId}-align-right-btn`} aria-pressed={state.align === "right"} aria-label={t("htmlEditor.alignRight")}>
          <AlignRight className="h-3.5 w-3.5" />
        </Button>

        <div className="mx-1 h-5 w-px bg-border" />

        <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().undo().run())} disabled={disabled}
                data-testid={`${testId}-undo-btn`} aria-label={t("htmlEditor.undo")}>
          <Undo2 className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                onClick={run(() => editor.chain().focus().redo().run())} disabled={disabled}
                data-testid={`${testId}-redo-btn`} aria-label={t("htmlEditor.redo")}>
          <Redo2 className="h-3.5 w-3.5" />
        </Button>

        {availableFields.length > 0 && (
          <>
            <div className="mx-1 h-5 w-px bg-border" />
            <Select key={insertKey} onValueChange={insertField} disabled={disabled}>
              <SelectTrigger className="h-7 w-44 text-xs" data-testid={`${testId}-insert-field`}>
                <SelectValue placeholder={t("htmlEditor.insertField")} />
              </SelectTrigger>
              <SelectContent>
                {availableFields.map((f) => (
                  <SelectItem key={f.key} value={f.key} data-testid={`${testId}-insert-field-${f.key}`}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>
      <EditorContent
        editor={editor}
        className="focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-background rounded-b-md"
      />
    </div>
  );
}
